/**
 * Orbital layout: Patient Satisfaction + Data Quality (left), center AverageGauge + 4 KpiRings, SubMetricsPanel (right).
 * Uses SBI Core data.
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import MDTypography from "components/MDTypography";
import AverageGauge from "./AverageGauge";
import KpiRing from "./KpiRing";
import SubMetricsPanel from "./SubMetricsPanel";
import GroupedBarChart from "../../core/components/GroupedBarChart";
import { useSbiCoreData } from "../../core/hooks/useSbiCoreData";
import { ME_COLOR, BENCHMARK_COLOR, LABEL_ME, LABEL_BENCHMARK } from "../constants";

// Which patient comfort metrics to show for each Quattro metric (by index 0–3)
const QUATTRO_TO_COMFORT = [
  ["Delirium / PND", "Burst Suppression Time"],
  ["Nausea / Vomiting", "High Thirst", "Long Fluid Fasting Time"],
  ["Shivering", "High Pain", "Throat/Airway discomfort"],
  ["High Anxiety", "High Stress", "Reduced well-being"],
];

// Demo palette (light ice blue -> deep teal) – shared by dark and light
const KPI_COLORS = ["#c3dce3", "#c3dce3", "#c3dce3", "#c3dce3"];
// SBI dark panel background (header and all panels)
const HEADER_BG_DARK = "#1e2d2f";

// Light theme palette (from data-visualization-comparison-lighttheme reference)
const LIGHT_PALETTE = {
  panelBg: "#e8f2f3",
  cardBg: "#e8f2f3",
  cardBorder: "#d1e0e2",
  textPrimary: "#1a3d40",
  textSecondary: "#5a7a7d",
  trackStroke: "rgba(0,0,0,0.12)",
  connectorStroke: "#d1e0e2",
  titleColor: "#1a3d40",
  axisColor: "#5a7a7d",
  gridColor: "rgba(0,0,0,0.08)",
  barTrackBg: "rgba(0,0,0,0.06)",
  gaugeValueColor: ME_COLOR,
  deltaPositive: { bg: "rgba(45, 212, 191, 0.15)", color: "#0f766e" },
  deltaNegative: { bg: "rgba(248, 113, 113, 0.15)", color: "#dc2626" },
  panelBorder: "rgba(0,0,0,0.08)",
  subMetricCardBg: "#ffffff",
  subMetricCardHoverBg: "rgba(0,0,0,0.03)",
};

const TARGET_BENCHMARK = 80;
const SUBMETRIC_TARGET = 20; // for “problem” metrics (lower % is better)

function computeAverage(values) {
  if (!values.length) return 0;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

const POSITION_STYLES = [
  { top: 0, left: 0 },
  { top: 0, right: 0 },
  { bottom: 0, right: 0 },
  { bottom: 0, left: 0 },
];

const CONNECTOR_ANGLES = [
  { x1: "33%", y1: "33%", x2: "24%", y2: "24%" },
  { x1: "67%", y1: "33%", x2: "76%", y2: "24%" },
  { x1: "67%", y1: "62%", x2: "76%", y2: "70%" },
  { x1: "33%", y1: "62%", x2: "24%", y2: "70%" },
];

export default function KpiDashboard({ dateFilter = { type: "all" }, theme: themeProp = "dark", benchmarkMode = "overall", panelBg: panelBgOverride }) {
  const { t } = useTranslation();
  const isLight = themeProp === "light";
  const palette = isLight ? LIGHT_PALETTE : null;
  const HEADER_BG = isLight
    ? (panelBgOverride ?? LIGHT_PALETTE.panelBg)
    : (panelBgOverride ?? HEADER_BG_DARK);
  const connectorStroke = isLight ? LIGHT_PALETTE.connectorStroke : "rgba(255,255,255,0.25)";
  const titleColor = isLight ? LIGHT_PALETTE.textPrimary : "#fff";
  const subtitleColor = isLight ? LIGHT_PALETTE.textSecondary : "rgba(255,255,255,0.7)";
  const cardBorder = isLight ? LIGHT_PALETTE.cardBorder : "rgba(255,255,255,0.1)";

  const {
    quattroMetrics,
    patientComfortMetrics,
    patientSatisfactionMetrics,
    dataQualityMetrics,
    nLabel,
    weeklyPatientComfortForTrends,
  } = useSbiCoreData(dateFilter, benchmarkMode);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const kpis = useMemo(() => {
    const metrics = quattroMetrics?.metrics ?? [];
    const list = [];
    for (let i = 0; i < 4; i++) {
      const m = metrics[i];
      const label = m?.label ?? `Quattro ${i + 1}`;
      const variationA = Math.max(0, Math.min(100, Number(m?.value) ?? 0));
      const variationB = m?.benchmarkValue != null ? m.benchmarkValue : TARGET_BENCHMARK;

      const comfortTitles = QUATTRO_TO_COMFORT[i] || [];
      const subMetrics = comfortTitles.map((title) => {
        const comfort = patientComfortMetrics?.find((c) => c.title === title);
        const value = comfort?.value ?? 0;
        const variationASub = typeof value === "number" ? value : 0;
        const variationBSub =
          benchmarkMode === "regional" && comfort?.regionalTarget != null
            ? comfort.regionalTarget
            : (comfort?.benchmarkValue ?? SUBMETRIC_TARGET);
        return {
          label: title,
          variationA: variationASub,
          variationB: variationBSub,
          count: comfort?.count ?? 0,
        };
      });

      list.push({
        label,
        variationA,
        variationB,
        subMetrics,
      });
    }
    return list;
  }, [quattroMetrics, patientComfortMetrics, benchmarkMode]);

  const avgA = useMemo(
    () => computeAverage(kpis.map((k) => k.variationA)),
    [kpis],
  );
  const avgB = useMemo(
    () => computeAverage(kpis.map((k) => k.variationB)),
    [kpis],
  );

  const selectedKpi = selectedIndex !== null ? kpis[selectedIndex] : null;
  const selectedColor =
    selectedIndex !== null ? KPI_COLORS[selectedIndex] : undefined;

  const orbContainerRef = useRef(null);
  const [orbSize, setOrbSize] = useState(560);
  const ORB_MIN = 560;
  const ORB_MAX = 720;

  useEffect(() => {
    const el = orbContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect ?? {};
      if (width > 0 && height > 0) {
        const size = Math.min(width, height, ORB_MAX);
        setOrbSize(Math.max(ORB_MIN, Math.round(size)));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const gaugeSize = Math.round(220 * (orbSize / 560));
  const ringSize = Math.round(130 * (orbSize / 560));

  return (
    <Box sx={{ width: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          flex: 1,
          minHeight: 0,
          alignItems: { xs: "flex-start", xl: "stretch" },
          justifyContent: "center",
          flexDirection: { xs: "column", xl: "row" },
          gap: 3,
        }}
      >
        {/* Left: 25% of available width */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            flex: { xs: "none", xl: "1 1 0" },
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              "& > *": { flex: 1, minHeight: 0 },
            }}
          >
            <GroupedBarChart
              title={t("dashboard.patientSatisfaction")}
              metrics={patientSatisfactionMetrics ?? []}
              cardBg={HEADER_BG}
              cardBorder={cardBorder}
              benchmarkColor={BENCHMARK_COLOR}
              meColor={ME_COLOR}
              titleColor={isLight ? LIGHT_PALETTE.titleColor : undefined}
              axisColor={isLight ? LIGHT_PALETTE.axisColor : undefined}
              gridColor={isLight ? LIGHT_PALETTE.gridColor : undefined}
              infoTooltip="Covers patient experience across the pre-medication talk, induction of anesthesia, and PACU stay."
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              "& > *": { flex: 1, minHeight: 0 },
            }}
          >
            <GroupedBarChart
              title={t("dashboard.dataQuality")}
              metrics={dataQualityMetrics ?? []}
              cardBg={HEADER_BG}
              cardBorder={cardBorder}
              benchmarkColor={BENCHMARK_COLOR}
              meColor={ME_COLOR}
              titleColor={isLight ? LIGHT_PALETTE.titleColor : undefined}
              axisColor={isLight ? LIGHT_PALETTE.axisColor : undefined}
              gridColor={isLight ? LIGHT_PALETTE.gridColor : undefined}
              infoTooltip="Internal consistency, quality of evaluation, and completeness of data."
            />
          </Box>
        </Box>

        {/* Center: 50% of available width; panel fills full column height */}
        <Box
          sx={{
            display: "flex",
            alignSelf: "stretch",
            flex: { xs: "none", xl: "2 2 0" },
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "100%",
              minHeight: 0,
              bgcolor: HEADER_BG,
              borderRadius: 2,
              border: `1px solid ${cardBorder}`,
              overflow: "hidden",
              p: 3,
            }}
          >
          <Box sx={{ flexShrink: 0, mb: 1.5 }}>
            <Box
              component="h2"
              sx={{
                fontSize: "1.25rem",
                fontWeight: 700,
                color: titleColor,
                m: 0,
              }}
            >
              {t("dashboard.quattroMetrics")}
            </Box>
            <Box
              component="p"
              sx={{
                fontSize: "0.8125rem",
                color: subtitleColor,
                m: 0,
                mt: 0.25,
              }}
            >
              Comparing actual results against target benchmarks. Select a KPI
              to explore sub-metrics.
            </Box>
            {nLabel && (
              <MDTypography variant="caption" sx={{ color: subtitleColor, mt: 0.5, display: "block" }}>
                {nLabel}
              </MDTypography>
            )}
          </Box>
          <Box
            ref={orbContainerRef}
            sx={{
              flex: 1,
              minHeight: ORB_MIN,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box position="relative" width={orbSize} height={orbSize}>
              {/* Connector lines */}
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 560 560"
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                }}
              >
                {CONNECTOR_ANGLES.map((c, i) => (
                  <line
                    key={i}
                    x1={c.x1}
                    y1={c.y1}
                    x2={c.x2}
                    y2={c.y2}
                    stroke={
                      selectedIndex === i
                        ? KPI_COLORS[i]
                        : connectorStroke
                    }
                    strokeWidth={selectedIndex === i ? 2 : 1}
                    strokeDasharray={selectedIndex === i ? "0" : "4 4"}
                    style={{ transition: "all 0.3s ease" }}
                  />
                ))}
              </svg>

              {/* Center: Average gauge */}
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <AverageGauge
                  variationA={avgA}
                  variationB={avgB}
                  labelA={LABEL_ME}
                  labelB={LABEL_BENCHMARK}
                  size={gaugeSize}
                  palette={palette}
                  meColor={ME_COLOR}
                  benchmarkColor={BENCHMARK_COLOR}
                />
              </Box>

              {/* 4 satellite KPI rings */}
              {kpis.map((kpi, index) => (
                <Box
                  key={kpi.label}
                  sx={{
                    position: "absolute",
                    ...POSITION_STYLES[index],
                    transition: "transform 0.3s ease",
                  }}
                >
                  <KpiRing
                    label={kpi.label}
                    variationA={kpi.variationA}
                    variationB={kpi.variationB}
                    isSelected={selectedIndex === index}
                    onSelect={() =>
                      setSelectedIndex(selectedIndex === index ? null : index)
                    }
                    size={ringSize}
                    palette={palette}
                    meColor={ME_COLOR}
                    benchmarkColor={BENCHMARK_COLOR}
                  />
                </Box>
              ))}
            </Box>
          </Box>
          </Box>
        </Box>

        {/* Right: 25% of available width */}
        <Box
          sx={{
            flex: { xs: "none", xl: "1 1 0" },
            minWidth: 0,
            display: { xl: "flex" },
            flexDirection: { xl: "column" },
          }}
        >
          <Box
            sx={{
              position: { xl: "sticky" },
              top: 24,
              flex: { xl: 1 },
              minHeight: 0,
              display: { xl: "flex" },
              flexDirection: { xl: "column" },
              height: { xl: "100%" },
            }}
          >
              <SubMetricsPanel
                kpi={selectedKpi}
                labelA={LABEL_ME}
                labelB={LABEL_BENCHMARK}
                meColor={ME_COLOR}
                benchmarkColor={BENCHMARK_COLOR}
                trendButtonColor={BENCHMARK_COLOR}
                fillHeight
                panelBg={HEADER_BG}
                palette={palette}
                weeklyPatientComfort={weeklyPatientComfortForTrends}
                benchmarkMode={benchmarkMode}
              />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
